import { useState, useMemo, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { FileText, Plus, Search, BookOpen, Clock, Trash2, Edit3, Check } from 'lucide-react'
import { useSpace } from '../hooks'
import { cardClassName } from '@jira-clone/ui'
import { cn } from '../utils'

interface DocItem {
  id: string
  title: string
  content: string
  author: string
  updatedAt: string
}

const DEFAULT_DOCS: DocItem[] = [
  {
    id: 'doc-1',
    title: 'Project Architecture & Standards',
    content:
      'Overview of repository architecture, folder conventions, commit rules and coding guidelines for the space.\n\n- Monorepo structured with pnpm and turbo\n- Frontend: React 18, Vite, TailwindCSS, TanStack Query\n- Backend: Fastify, Prisma ORM, MongoDB fallback\n- All components should use typed interfaces and clean reactive hooks',
    author: 'Lead Architect',
    updatedAt: '2 days ago',
  },
  {
    id: 'doc-2',
    title: 'Release & Sprint Planning Checklist',
    content:
      'Steps to follow before closing sprints, creating release tags, and updating deployment configurations.\n\n1. Review all open issues in current active sprint\n2. Move incomplete work items back to backlog or next sprint\n3. Run end-to-end integration and smoke tests\n4. Trigger release pipeline build',
    author: 'Scrum Master',
    updatedAt: '5 days ago',
  },
  {
    id: 'doc-3',
    title: 'API Integration & Webhook Endpoints',
    content:
      'Specification of available REST & WebSocket event endpoints supported by this space service.\n\n- GET /api/v1/goodday/tasks?projectId=:id\n- POST /api/v1/goodday/tasks\n- PATCH /api/v1/goodday/tasks/:id\n- DELETE /api/v1/goodday/tasks/:id',
    author: 'Backend Team',
    updatedAt: '1 week ago',
  },
]

export function DocsPage() {
  const params = useParams<{ id?: string; spaceId?: string }>()
  const spaceId = params.id || params.spaceId || ''
  const { data: space } = useSpace(spaceId ?? null)

  const storageKey = `goodday_space_docs_${spaceId || 'default'}`

  const [docs, setDocs] = useState<DocItem[]>(() => {
    if (typeof window !== 'undefined' && spaceId) {
      try {
        const saved = localStorage.getItem(`goodday_space_docs_${spaceId}`)
        if (saved) return JSON.parse(saved)
      } catch {
        // fallback
      }
    }
    return DEFAULT_DOCS
  })

  // Keep docs in sync with localStorage for the space
  useEffect(() => {
    if (typeof window !== 'undefined' && spaceId) {
      try {
        const saved = localStorage.getItem(storageKey)
        if (saved) {
          setDocs(JSON.parse(saved))
        } else {
          setDocs(DEFAULT_DOCS)
        }
      } catch {
        // fallback
      }
    }
  }, [spaceId, storageKey])

  const persistDocs = (newDocs: DocItem[]) => {
    setDocs(newDocs)
    if (typeof window !== 'undefined' && spaceId) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(newDocs))
      } catch {
        // ignore
      }
    }
  }

  const [search, setSearch] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedDoc, setSelectedDoc] = useState<DocItem | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')

  const filteredDocs = useMemo(() => {
    return docs.filter(
      (d) =>
        !search ||
        d.title.toLowerCase().includes(search.toLowerCase()) ||
        d.content.toLowerCase().includes(search.toLowerCase()),
    )
  }, [docs, search])

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) return
    const newDoc: DocItem = {
      id: `doc-${Date.now()}`,
      title: title.trim(),
      content: content.trim() || 'No content yet.',
      author: 'You',
      updatedAt: 'Just now',
    }
    persistDocs([newDoc, ...docs])
    setTitle('')
    setContent('')
    setModalOpen(false)
  }

  const handleDelete = (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    const next = docs.filter((d) => d.id !== id)
    persistDocs(next)
    if (selectedDoc?.id === id) setSelectedDoc(null)
  }

  const handleSaveEdit = () => {
    if (!selectedDoc) return
    const next = docs.map((d) =>
      d.id === selectedDoc.id
        ? { ...d, title: selectedDoc.title, content: selectedDoc.content, updatedAt: 'Just now' }
        : d,
    )
    persistDocs(next)
    setIsEditing(false)
  }

  return (
    <div className="flex h-full flex-col overflow-y-auto p-6 bg-white dark:bg-slate-950 space-y-6 font-sans">
      {/* Top Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-blue-600" />
            Documentation — {space?.name || 'Space'}
          </h2>
          <p className="text-xs text-gray-500 dark:text-slate-400">
            Project wiki, guidelines, meeting notes, and knowledge base
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search docs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 rounded-lg border border-gray-200 pl-8 pr-3 text-xs dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500 w-52"
            />
          </div>

          <button
            onClick={() => setModalOpen(true)}
            className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-blue-700"
          >
            <Plus className="h-3.5 w-3.5" /> New Doc
          </button>
        </div>
      </div>

      {/* Docs Grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {filteredDocs.map((doc) => (
          <div
            key={doc.id}
            onClick={() => {
              setSelectedDoc(doc)
              setIsEditing(false)
            }}
            className={cn(
              cardClassName({ interactive: true }),
              'group flex flex-col justify-between p-5 transition-all hover:border-blue-400 dark:hover:border-slate-700',
              selectedDoc?.id === doc.id && 'ring-2 ring-blue-500',
            )}
          >
            <div>
              <div className="flex items-start justify-between gap-2">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400">
                  <FileText className="h-4 w-4" />
                </span>
                <button
                  onClick={(e) => handleDelete(doc.id, e)}
                  title="Delete doc"
                  className="opacity-0 group-hover:opacity-100 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-600 dark:hover:bg-slate-800 transition-opacity"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>

              <h3 className="mt-3 text-sm font-bold text-gray-900 dark:text-slate-100 group-hover:text-blue-600 transition-colors">
                {doc.title}
              </h3>
              <p className="mt-1 line-clamp-3 text-xs text-gray-500 dark:text-slate-400 leading-relaxed whitespace-pre-line">
                {doc.content}
              </p>
            </div>

            <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-3 text-[11px] text-gray-400 dark:border-slate-800/80">
              <span className="font-medium text-gray-600 dark:text-slate-400">{doc.author}</span>
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" /> {doc.updatedAt}
              </span>
            </div>
          </div>
        ))}

        {filteredDocs.length === 0 && (
          <div className="col-span-full py-12 text-center">
            <BookOpen className="h-8 w-8 text-gray-300 dark:text-slate-700 mx-auto mb-2" />
            <p className="text-sm font-semibold text-gray-500">No documents found</p>
            <p className="text-xs text-gray-400 mt-1">Create your first document to build your space knowledge base.</p>
          </div>
        )}
      </div>

      {/* Doc Preview / Edit Modal */}
      {selectedDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-in fade-in">
          <div className="w-full max-w-2xl rounded-xl bg-white p-6 shadow-2xl dark:bg-slate-900 border border-gray-200 dark:border-slate-800 max-h-[85vh] flex flex-col">
            <div className="flex items-start justify-between border-b border-gray-100 pb-3 dark:border-slate-800">
              <div className="flex-1 mr-4">
                {isEditing ? (
                  <input
                    type="text"
                    value={selectedDoc.title}
                    onChange={(e) => setSelectedDoc({ ...selectedDoc, title: e.target.value })}
                    className="w-full text-base font-bold text-gray-900 dark:text-slate-100 rounded border border-gray-200 dark:border-slate-700 px-2 py-1 outline-none focus:ring-1 focus:ring-blue-500"
                  />
                ) : (
                  <>
                    <h3 className="text-base font-bold text-gray-900 dark:text-slate-100">{selectedDoc.title}</h3>
                    <p className="text-xs text-gray-400 mt-0.5">
                      By {selectedDoc.author} • {selectedDoc.updatedAt}
                    </p>
                  </>
                )}
              </div>

              <div className="flex items-center gap-2">
                {isEditing ? (
                  <button
                    onClick={handleSaveEdit}
                    className="flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                  >
                    <Check className="h-3.5 w-3.5" /> Save
                  </button>
                ) : (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center gap-1 rounded-lg border border-gray-200 dark:border-slate-700 px-2.5 py-1 text-xs font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800"
                  >
                    <Edit3 className="h-3.5 w-3.5" /> Edit
                  </button>
                )}
                <button
                  onClick={() => setSelectedDoc(null)}
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="mt-4 flex-1 overflow-y-auto">
              {isEditing ? (
                <textarea
                  rows={12}
                  value={selectedDoc.content}
                  onChange={(e) => setSelectedDoc({ ...selectedDoc, content: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 dark:border-slate-700 p-3 text-sm text-gray-800 dark:text-slate-200 outline-none focus:ring-1 focus:ring-blue-500 font-sans leading-relaxed"
                />
              ) : (
                <div className="text-sm text-gray-700 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">
                  {selectedDoc.content}
                </div>
              )}
            </div>

            <div className="mt-4 flex justify-between items-center border-t border-gray-100 pt-3 dark:border-slate-800">
              <button
                onClick={(e) => handleDelete(selectedDoc.id, e)}
                className="flex items-center gap-1 text-xs text-red-600 hover:underline"
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete Doc
              </button>
              <button
                onClick={() => setSelectedDoc(null)}
                className="rounded-lg bg-gray-100 px-4 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-200 dark:bg-slate-800 dark:text-slate-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Doc Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-in fade-in">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl dark:bg-slate-900 border border-gray-200 dark:border-slate-800">
            <h3 className="text-base font-bold text-gray-900 dark:text-slate-100">Create New Doc</h3>
            <form onSubmit={handleCreate} className="mt-4 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1">
                  Document Title
                </label>
                <input
                  type="text"
                  required
                  autoFocus
                  placeholder="e.g. Deployment Guide"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1">
                  Content (Markdown / Text)
                </label>
                <textarea
                  rows={6}
                  placeholder="Write doc content..."
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 p-3 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-slate-700 dark:text-slate-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-blue-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 shadow-xs"
                >
                  Save Document
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
